<template>
  <div class="user-profile-section">
    <section class="user-profile-card p-3 mx-auto">
      <div class="user-profile-card__header rounded-sm">
        <h5 class="m-0">Perfil</h5><h5 class="m-0"><i class="fad fa-user-circle"></i></h5>
      </div>

      <div class="user-profile-fixed">
        <p>{{ form.name }}</p>
        <p :class="{ muted: !form.email}">{{ form.email || 'Sin correo' }}</p>
        <p :class="{ muted: !form.cellPhone}">{{ form.cellPhone?.number || 'Sin número' }}</p>
        <b-button
          class="change-password-btn"
          variant="outline-secondary"
          title="Cambio de contraseña"
          @click="$emit('show-change-password')">
          <i class="fas fa-key"></i>
        </b-button>
      </div>

      <b-form class="user-profile-form p-4 pt-3">
        <b-button
          v-if="readOnly"
          class="edit-profile-btn"
          variant="outline-secondary"
          @click="readOnly=false"
          title="Editar perfil">
          <i class="fas fa-pen"></i>
        </b-button>
        <b-form-group
          label="Nombre de la persona"
          label-for="first-name-info">
          <b-form-input
            id="first-name-info"
            v-model.trim="profile.firstName"
            placeholder="Nombre de la persona"
            :disabled="readOnly">
          </b-form-input>
        </b-form-group>

        <b-form-group
          label="Apellido(s) de la persona"
          label-for="last-name-info">
          <b-form-input
            id="last-name-info"
            v-model.trim="profile.lastName"
            placeholder="Apellido(s) de la persona"
            :disabled="readOnly">
          </b-form-input>
        </b-form-group>

        <b-form-group
          label="Correo extra"
          label-for="email-extra-info">
          <b-form-input
            id="email-extra-info"
            v-model.trim="profile.emailExtra"
            :state="readOnly ? null : validateEmail"
            placeholder="Ej: correo_extra@mail.com"
            type="email"
            :disabled="readOnly"
            aria-describedby="email-extra-feedback"
            autocomplete="off"
            >
          </b-form-input>
          <b-form-invalid-feedback id="email-extra-feedback">
            Introduzca un correo válido
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="Teléfono extra"
          label-for="phone-extra-info">
          <b-form-input
            id="phone-extra-info"
            v-model.trim="profile.phoneExtra"
            :state="readOnly ? null : validatePhone"
            placeholder="Ej: +525512345678 ó 5512345678"
            :disabled="readOnly"
            aria-describedby="phone-extra-feedback"
            >
          </b-form-input>
          <b-form-invalid-feedback id="phone-extra-feedback">
            Introduzca un teléfono válido (Ej: +525512345678 ó 5512345678)
          </b-form-invalid-feedback>
        </b-form-group>

        <b-row class="m-0">
          <b-button
            v-if="!readOnly"
            class="user-edit-profile-button mr-2"
            variant="outline-secondary"
            @click="cancelUpdateProfile">
            Cancelar
          </b-button>
          <b-button
            v-if="!readOnly"
            class="user-edit-profile-button"
            variant="primary"
            :disabled="!validateFormatValues"
            @click="updateProfile">
            Guardar
          </b-button>
        </b-row>
      </b-form>
    </section>
  </div>

</template>

<script lang="ts">
import api from '@/api'
import config from '@/config'
import AuthService from '@/services/support/AuthService'
import userInfoStore from '@/store/userInfo'
import { UserInfo } from '@/types/Auth'
import { GENERAL_REGEXPS } from '@/types/RegExpressions'
import Vue from 'vue'
import Component from 'vue-class-component'

interface UserProfileData {
  firstName: string
  lastName: string
  phoneExtra: string
  emailExtra: string
}

@Component({
  name: 'UserProfile'
})
class UserProfile extends Vue {
  form: UserInfo = new UserInfo()
  readOnly: boolean = true
  backupProfile: UserProfileData = {
    firstName: '',
    lastName: '',
    emailExtra: '',
    phoneExtra: ''
  }
  profile: UserProfileData = {
    firstName: '',
    lastName: '',
    emailExtra: '',
    phoneExtra: ''
  }

  created () {
    this.loadProfile()
  }

  /**
   * Sincronizes the backup and the profil user with the current user info
   *
   * @param userInfo with current values
   */
  sincronizedUserInfo (userInfo: UserInfo) {
    this.form = userInfo
    this.setProfile(this.backupProfile, this.form)
    this.setProfile(this.profile, this.form)
  }

  /**
   * Gets the user info and sincronized its values
   * with two objects: the backup and the profile object
   */
  async loadProfile () {
    await AuthService.getUserInfo()
      .then(() => {
        this.sincronizedUserInfo(userInfoStore.userInfo)
      })
  }

  /**
   * Assigns the profile values to a specific target
   *
   * @param target to set the values
   * @param profile from get values to set
   */
  setProfile (target: UserProfileData, profile: UserProfileData) {
    target.firstName = profile.firstName || ''
    target.lastName = profile.lastName || ''
    target.emailExtra = profile.emailExtra || ''
    target.phoneExtra = profile.phoneExtra || ''
  }

  /**
   * Validates if has change some value in the form
   */
  profileHasChanged () {
    return JSON.stringify(this.profile) !== JSON.stringify(this.backupProfile)
  }

  /**
   * Make the request to save the values
   *
   * @param profile with the current values
   */
  async saveProfile (profile: UserProfileData) {
    const url = `${config.apiUsersUrl}me`
    const data = {
      firstName: profile.firstName || null,
      lastName: profile.lastName || null,
      emailExtra: profile.emailExtra || null,
      phoneExtra: profile.phoneExtra || null
    }
    const request = await api.put(url, data)
    return request.data
  }

  /**
   * Validates that exist some change in the form
   * and make the request to update the values
   */
  async updateProfile () {
    // Only when sme value has changed
    if (this.profileHasChanged()) {
      this.saveProfile(this.profile)
        // display a feedback and reload the user info
        .then((response) => {
          this.$bvToast.toast(
            'El perfil ha sido actualizado correctamente.',
            {
              title: 'Cambios guardados',
              solid: true,
              variant: 'success',
              autoHideDelay: 4000,
              appendToast: true
            }
          )
          this.sincronizedUserInfo(response)
          this.readOnly = true
        })
        // manage the error
        .catch(() => {
          this.$bvToast.toast(
            `No ha sido posible guardar los cambios.
             Revisa los campos marcados con error.`,
            {
              title: 'Cambios no guardados',
              solid: true,
              variant: 'danger',
              autoHideDelay: 4000,
              appendToast: true
            }
          )
        })
    } else {
      this.readOnly = true
    }
  }

  /**
   * On cancel the update profile
   * If the profile has some change reload the backup values
   */
  cancelUpdateProfile () {
    if (this.profileHasChanged()) {
      this.profile = { ...this.backupProfile }
    }
    this.readOnly = true
  }

  /** Validates the email extra format */
  get validateEmail () {
    let email = this.profile.emailExtra
    return email
      ? new RegExp(GENERAL_REGEXPS.email).test(email)
      : true
  }

  /** Validates the phone extra format */
  get validatePhone () {
    let phone = this.profile.phoneExtra
    return phone
      ? new RegExp(GENERAL_REGEXPS.phone).test(phone)
      : true
  }

  /** Validates the email and phone satisfy the formats */
  get validateFormatValues () {
    return this.validateEmail && this.validatePhone
  }
}
export default UserProfile
</script>

<style>
.user-profile-section {
  width: 100%;
  height: 100%;
}
.user-profile-card {
  max-width: 32rem;
  min-width: 300px;
}
.user-profile-card__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: #e9ecef;
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 5;
}
.user-profile-fixed {
  position: relative;
  text-align: center;
  padding: 1.5rem 1.5rem 0 1.5rem;
}
.user-profile-fixed > .change-password-btn {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  border-radius: 0.75rem;
  z-index: 0;
}
.user-profile-fixed > p {
  margin: 0 0 0.2rem 0;
}
.user-profile-form {
  position: relative;
}
.user-profile-form > .edit-profile-btn {
  position: absolute;
  top: -1rem;
  right: 0;
  margin: 1.5rem;
  border-radius: 0.75rem;
}
.user-edit-profile-button {
  width: 49%;
}
.muted {
  opacity: 0.4;
}
</style>
